import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getCopturePrices } from "../../../api/services/drone";
import { trackDroneCheckoutEvent } from "../comps/DroneCheckout/utils";
import { usePropertyFetcher } from "../hooks/usePropertyFetcher";
import { PropsData } from "../context";
import {
  mapPropertiesData,
  saveOrderToCookie,
  getOrderFromCookie,
  clearOrderFromCookie,
} from "../comps/DroneCheckout/utils";
import { calculatePrices } from "../comps/DroneCheckout/helpers";

const DroneContext = createContext();

export const DroneProvider = ({
  children,
  totalSteps,
  initialStep,
  isOpen,
}) => {
  const propsContext = useContext(PropsData);
  const { fetchPropData } = usePropertyFetcher();

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(isOpen);

  const [userProperties, setUserProperties] = useState([]);
  const [dronePrices, setDronePrices] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(undefined);
  const [selectedForestManagementPlan, setSelectedForestManagementPlan] =
    useState(undefined);
  const [couponDiscount, setCouponDiscount] = useState(undefined);
  const [extraData, setExtraData] = useState({});

  const [orderPrices, setOrderPrices] = useState({
    discountPrice: undefined,
    fullPrice: undefined,
    propertiesTotalPrice: undefined,
    propertiesHectarePrice: undefined,
    packageTotalPrice: undefined,
    packageHectarePrice: undefined,
    forestManagementPlanTotalPrice: undefined,
    tax: undefined,
  });

  const [paymentMethod, setPaymentMethod] = useState(undefined);

  const [errorMessages, setErrorMessages] = useState({});

  useEffect(() => {
    setIsCheckoutOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setCurrentStep(initialStep);

    if (initialStep === 4) {
      const order = getOrderFromCookie();

      if (order) {
        setOrderPrices(order.orderPrices);
        setPaymentMethod(order.paymentMethod);
        setCouponDiscount(order.couponDiscount);
        setSelectedProperties(order.selectedProperties);
        setSelectedPackage(order.selectedPackage);
        setSelectedForestManagementPlan(order.selectedForestManagementPlan);
        setExtraData(order.extraData);

        clearOrderFromCookie();
      }
    }
  }, [initialStep]);

  useEffect(() => {
    // Only saves order to cookie on payment step + payment method is set to klarna
    if (currentStep === 3 && paymentMethod === "klarna") {
      saveOrderToCookie({
        selectedProperties,
        selectedPackage,
        selectedForestManagementPlan,
        extraData,
        orderPrices,
        paymentMethod,
        couponDiscount,
      });
    }
  }, [currentStep]);

  useEffect(() => {
    // Only run when checkout is open
    if (!isCheckoutOpen) return;
    trackDroneCheckoutEvent("step_initiated", currentStep);

    if (currentStep === 4) {
      trackDroneCheckoutEvent("completed", currentStep);
    }

    const fetchDronePrices = async () => {
      const { ok, status, data } = await getCopturePrices();
      if (ok) {
        setDronePrices(data);
      } else {
        console.error("Price fetch error: ", status, data);
      }
    };

    const fetchProperties = async () => {
      const mappedProperties = mapPropertiesData(propsContext?.props);
      mappedProperties.forEach((prop) => {
        if (!prop.area) {
          fetchPropData(propsContext.setOneProp, prop);
        }
      });
      setUserProperties(mappedProperties);
    };

    fetchProperties();
    fetchDronePrices();
  }, [isCheckoutOpen]);

  useEffect(() => {
    if (!isCheckoutOpen) return;

    if (dronePrices?.products?.length > 0 && selectedProperties.length > 0) {
      const priceCalculation = calculatePrices({
        properties: selectedProperties,
        selectedPackage,
        selectedForestManagementPlan,
        couponDiscount,
        dronePrices,
      });

      setPaymentMethod(
        priceCalculation.discountedPrice > 20000 ? "invoice" : "klarna"
      );

      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: priceCalculation.discountedPrice,
        fullPrice: priceCalculation.fullPrice,
        tax: 0.25 * priceCalculation.discountedPrice,
        propertiesHectarePrice: priceCalculation.propertiesHectarePrice,
        propertiesTotalPrice: priceCalculation.propertiesTotalPrice,
        packageHectarePrice: priceCalculation.packageHectarePrice,
        packageTotalPrice: priceCalculation.packageTotalPrice,
        forestManagementPlanTotalPrice:
          priceCalculation.forestManagementPlanTotalPrice,
      }));
    } else {
      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: undefined,
        fullPrice: undefined,
      }));
    }
  }, [
    isCheckoutOpen,
    dronePrices,
    selectedProperties,
    selectedPackage,
    selectedForestManagementPlan,
    couponDiscount,
  ]);

  // Memoize functions to prevent unnecessary re-renders
  const openCheckout = useCallback(() => setIsCheckoutOpen(true), []);
  const closeCheckout = useCallback(() => setIsCheckoutOpen(false), []);

  const addSelectedProperty = useCallback(
    (property) => {
      if (!selectedProperties.some((p) => p.ref === property.ref)) {
        setSelectedProperties([...selectedProperties, property]);
      }
    },
    [selectedProperties]
  );

  const removeSelectedProperty = useCallback(
    (property) =>
      setSelectedProperties(
        selectedProperties.filter((p) => p.ref !== property.ref)
      ),
    [selectedProperties]
  );

  const setCurrentStepValue = useCallback(
    (step) =>
      step >= 1 && step <= totalSteps ? setCurrentStep(step) : currentStep,
    [currentStep, totalSteps]
  );

  const nextStep = useCallback(() => {
    if (currentStep === totalSteps) {
      closeCheckout();
      resetCheckout();
      return;
    }

    const nextStep = currentStep + 1;

    setCurrentStepValue(nextStep);
    trackDroneCheckoutEvent("step_initiated", nextStep);

    if (nextStep === totalSteps) {
      trackDroneCheckoutEvent("completed", nextStep, {
        payment_method: paymentMethod,
      });
    }
  }, [currentStep, setCurrentStepValue, totalSteps]);
  const previousStep = useCallback(
    () => setCurrentStepValue(currentStep - 1),
    [currentStep, setCurrentStepValue]
  );

  const resetCheckout = useCallback(() => {
    closeCheckout();
    setCurrentStep(1);
    setSelectedProperties([]);
    setSelectedPackage(undefined);
    setSelectedForestManagementPlan(undefined);
    setErrorMessages({});
    setExtraData({});
    setCouponDiscount(undefined);
    setPaymentMethod(undefined);
    setOrderPrices({
      discountPrice: undefined,
      fullPrice: undefined,
      propertiesTotalPrice: undefined,
      propertiesHectarePrice: undefined,
      packageTotalPrice: undefined,
      packageHectarePrice: undefined,
      forestManagementPlanTotalPrice: undefined,
      tax: undefined,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      isCheckoutOpen,
      openCheckout,
      closeCheckout,

      // Property
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,

      // Package
      selectedPackage,
      setSelectedPackage,

      // Forest Management Plan
      selectedForestManagementPlan,
      setSelectedForestManagementPlan,

      // Coupon discount
      couponDiscount,
      setCouponDiscount,

      // Extra data
      extraData,
      setExtraData,

      // Step
      currentStep,
      totalSteps,
      nextStep,
      previousStep,

      // Order prices
      orderPrices,
      setOrderPrices,

      // Payment method
      paymentMethod,
      setPaymentMethod,

      // Property prices
      dronePrices,

      // Reset checkout
      resetCheckout,

      // Error messages
      errorMessages,
      setErrorMessages,
    }),
    [
      isCheckoutOpen,
      openCheckout,
      closeCheckout,
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,
      selectedPackage,
      setSelectedPackage,
      selectedForestManagementPlan,
      setSelectedForestManagementPlan,
      couponDiscount,
      setCouponDiscount,
      extraData,
      setExtraData,
      currentStep,
      totalSteps,
      nextStep,
      previousStep,
      orderPrices,
      setOrderPrices,
      resetCheckout,
      dronePrices,
      paymentMethod,
      setPaymentMethod,
      errorMessages,
      setErrorMessages,
    ]
  );

  return (
    <DroneContext.Provider value={contextValue}>
      {children}
    </DroneContext.Provider>
  );
};

export const useDrone = () => {
  const context = useContext(DroneContext);
  if (!context) {
    throw new Error("useDrone must be used within a DroneProvider");
  }
  return context;
};

export default DroneContext;
